import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { authApi } from '@/services/api/auth';
import { useAuth, MICROSOFT_PENDING_EMAIL_KEY, MICROSOFT_PENDING_USER_ID_KEY } from '@/contexts/AuthContext';

const MicrosoftCallbackPage = () => {
  const navigate = useNavigate();
  const { completeLoginWithToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const finishLogin = async (token: string, email: string) => {
      localStorage.removeItem(MICROSOFT_PENDING_EMAIL_KEY);
      localStorage.removeItem(MICROSOFT_PENDING_USER_ID_KEY);

      try {
        await completeLoginWithToken(token, email);
        navigate('/', { replace: true });
      } catch (err: any) {
        setError(err?.message || 'Login failed. Please try again.');
      }
    };

    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);

      const msError = params.get('error');
      if (msError) {
        setError(`Microsoft authentication failed: ${params.get('error_description') || msError}`);
        return;
      }

      const code = params.get('code');
      if (!code) {
        setError('Authentication failed. No authorization code received.');
        return;
      }

      const pendingEmail = localStorage.getItem(MICROSOFT_PENDING_EMAIL_KEY);
      const pendingUserId = localStorage.getItem(MICROSOFT_PENDING_USER_ID_KEY);

      if (!pendingEmail) {
        setError('Authentication session expired. Please sign in again.');
        return;
      }

      const redirectUri = `${window.location.origin}/auth/microsoft/callback`;

      try {
        const callbackData = await authApi.microsoftCallback(code, redirectUri, pendingEmail);

        if (!callbackData.requiresSelection) {
          if (!callbackData.token) {
            setError('Authentication failed. Please try again.');
            return;
          }
          await finishLogin(callbackData.token, pendingEmail);
          return;
        }

        const { selectionToken, accounts } = callbackData;
        if (!selectionToken || !accounts?.length) {
          setError('Authentication failed. Account selection data missing.');
          return;
        }

        const matchedAccount =
          accounts.find((a) => pendingUserId && String(a.userId) === pendingUserId) ??
          accounts.find((a) => a.username.toLowerCase() === pendingEmail.toLowerCase());

        if (!matchedAccount) {
          setError('No matching account found. Please sign in again.');
          return;
        }

        const selectData = await authApi.microsoftSelect(matchedAccount.userId, selectionToken);
        if (!selectData.token) {
          setError('Account selection failed. Please try again.');
          return;
        }

        await finishLogin(selectData.token, pendingEmail);
      } catch {
        setError('Authentication failed. Please try again.');
      }
    };

    handleCallback();
  }, [navigate, completeLoginWithToken]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4 max-w-sm px-4">
          <AlertCircle className="h-10 w-10 text-red-600 mx-auto" />
          <p className="text-gray-700">{error}</p>
          <a href="/" className="block text-sm text-emerald-700 underline">
            Return to Sign In
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center space-y-3">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-emerald-600" />
        <p className="text-sm text-gray-600">Completing Microsoft authentication...</p>
      </div>
    </div>
  );
};

export default MicrosoftCallbackPage;
